import {
  computed,
  effect,
  inject,
  Injectable,
  Signal,
  signal,
  untracked,
} from '@angular/core';
import {
  FilterCommand,
  FilterOperator,
  FilterSuffix,
  SortQuery,
} from '../../api/api.operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { cyrb53 } from '../hash';
import { toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { filter, startWith, Subject } from 'rxjs';
import {
  APP_FILTER_SERVICE_CONFIG,
  APP_FILTER_VIEWS,
  FilterQueryParams,
  FilterServiceConfig,
  SortState,
} from './filter.interfaces';
import { decode, encode } from 'universal-base64url';
import { arrayUniqueBy } from '../array';
import { unpackFilterValue } from './filter.util';

@Injectable()
export class FilterService {
  private readonly config: FilterServiceConfig =
    inject(APP_FILTER_SERVICE_CONFIG, { optional: true }) ?? {};

  private readonly _filters = signal<Record<string, any> | undefined>(
    undefined,
  );
  public readonly filters = this._filters.asReadonly();
  private readonly _sorts = signal<SortQuery[] | undefined>(undefined);
  public readonly sorts = this._sorts.asReadonly();
  private readonly _search = signal<string | undefined>(undefined);
  public readonly search = this._search.asReadonly();
  private readonly _views = signal<{
    view: string | undefined;
    views: Record<string, string[]>;
  }>({ view: undefined, views: {} });
  public readonly view = computed(() => this._views().view);
  private readonly presetViews =
    inject(APP_FILTER_VIEWS, { optional: true }) ?? {};
  public readonly views = computed(() => ({
    ...this.presetViews,
    ...this._views().views,
  }));
  public readonly viewColumns = computed(() => {
    const view = this.view();
    if (view) return this.views()[view];
    return undefined;
  });

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly url = toSignal(
    this.router.events.pipe(
      startWith(new NavigationEnd(0, '', '')),
      filter((event) => event instanceof NavigationEnd),
      map(() => location.pathname),
      distinctUntilChanged(),
    ),
  );

  private readonly queryParams = toSignal(
    this.route.queryParams.pipe(
      map((rawParams): FilterQueryParams => {
        let filters: Record<string, any> = {},
          filterStates: Record<string, any> = {},
          sorts: SortQuery[] = [],
          search: string = '';
        try {
          filters = JSON.parse(decode(rawParams['f']));
        } catch {}
        try {
          filterStates = JSON.parse(decode(rawParams['fs']));
        } catch {}
        try {
          sorts = JSON.parse(decode(rawParams['s']));
        } catch {}
        try {
          search = JSON.parse(decode(rawParams['q']));
        } catch {}
        return { filters, filterStates, sorts, search };
      }),
      distinctUntilChanged(
        (previous, current) =>
          JSON.stringify(previous) !== JSON.stringify(current),
      ),
    ),
  );
  readonly urlFilters = computed(() => this.queryParams()?.filters);
  readonly urlFilterStates = computed(() => this.queryParams()?.filterStates);
  readonly urlSorts = computed(() => this.queryParams()?.sorts);
  readonly urlSearch = computed(() => this.queryParams()?.search);

  private stateWatcher = new Subject<{ filter: string | null; state: any }>();

  get configKey(): string | undefined {
    const keyProvider = this.config.key;
    if (!keyProvider || typeof keyProvider === 'string') return keyProvider;
    return keyProvider();
  }

  constructor() {
    // Listen to URL and query param changes (if syncUrl) to reload the filters from storage and QP
    effect(
      () => {
        const url = this.url();
        if (url) {
          const filters = structuredClone(this.config.defaults?.filters ?? {});
          const storeFilters = localStorage.getItem(this.getStoreKey());

          if (storeFilters !== null && storeFilters !== 'null') {
            Object.assign(filters, JSON.parse(storeFilters));
          }
          if (this.config.syncUrl) {
            const urlFilters = this.urlFilters();
            const urlFilterState = this.urlFilterStates();
            if (urlFilterState) {
              // Save the state, but don't sync URL, so the effect listening to
              // this.filters (via `this._filters.set(...)` below) can sync them
              // both in the same operation. Otherwise, the first sync overwrites
              // and removes data before the 2nd sync happens, or vice versa.
              this.storeAllState(urlFilterState, false);
            }
            Object.assign(filters, urlFilters);
          }
          this._filters.set(filters);
        }
      },
      { allowSignalWrites: true },
    );
    // Listen to filter changes to write the filters to storage
    effect(
      () => {
        const filters = this.filters();
        if (this.configKey ?? untracked(this.url)) {
          this.overwriteStoredFilters(filters);
          if (this.config.syncUrl) this.syncUrl();
        }
      },
      { allowSignalWrites: true },
    );
    // Listen to URL and query param changes (if syncUrl) to reload the sorts from storage and QP
    effect(
      () => {
        const url = this.url();
        if (url) {
          const sorts: SortQuery[] = structuredClone(
            this.config.defaults?.sorts ?? [],
          );
          const storeSorts = localStorage.getItem(
            this.getStoreKey({ sort: true }),
          );

          if (storeSorts !== null && storeSorts !== 'null') {
            sorts.push(...JSON.parse(storeSorts));
          }
          if (this.config.syncUrl) {
            const urlSorts = this.urlSorts();
            if (urlSorts) sorts.push(...urlSorts);
          }
          // Reverse array so last added elements have prio, and unique by their field
          this._sorts.set(arrayUniqueBy(sorts.reverse(), (sort) => sort[0]));
        }
      },
      { allowSignalWrites: true },
    );
    // Listen to sort changes to write the filters to storage
    effect(
      () => {
        const sorts = this.sorts();
        if (this.configKey ?? untracked(this.url)) {
          this.overwriteStoredSorts(sorts);
          if (this.config.syncUrl) this.syncUrl();
        }
      },
      { allowSignalWrites: true },
    );
    // Listen to URL and query param changes (if syncUrl) to reload the sorts from storage and QP
    effect(
      () => {
        const url = this.url();
        if (url) {
          let search = this.config.defaults?.search ?? '';
          const storeSearch = localStorage.getItem(
            this.getStoreKey({ search: true }),
          );

          if (storeSearch !== null) {
            search = storeSearch;
          }
          if (this.config.syncUrl) {
            const urlSearch = this.urlSearch();
            if (urlSearch) search = urlSearch;
          }
          this._search.set(search);
        }
      },
      { allowSignalWrites: true },
    );
    // Listen to sort changes to write the filters to storage
    effect(
      () => {
        const search = this.search();
        if (this.configKey ?? untracked(this.url)) {
          this.overwriteStoredSearch(search);
          if (this.config.syncUrl) this.syncUrl();
        }
      },
      { allowSignalWrites: true },
    );
    // Listen to URL changes to reload the custom views from storage
    effect(
      () => {
        const url = this.url();
        if (url) {
          let views = { view: undefined, views: {} };
          const storeViews = localStorage.getItem(
            this.getStoreKey({ views: true }),
          );

          if (storeViews !== null) {
            views = JSON.parse(storeViews);
          }
          this._views.set(views);
        }
      },
      { allowSignalWrites: true },
    );
    // Listen to view changes to write the filters to storage
    effect(
      () => {
        const views = this._views();
        if (this.configKey ?? untracked(this.url)) {
          this.overwriteStoredViews(views);
        }
      },
      { allowSignalWrites: true },
    );
  }

  setFilter(
    filter: string,
    value: unknown,
    operator: FilterOperator = FilterOperator.LIKE,
    suffix?: FilterSuffix,
  ) {
    this._filters.update((filters) => {
      const shallowUpdate = (updated: object) =>
        Object.assign({}, filters, updated);
      if (value === null || operator === FilterOperator.NULL) {
        const tokens = suffix
          ? [suffix, FilterOperator.NULL]
          : [FilterOperator.NULL];
        return shallowUpdate({ [filter]: tokens });
      } else if (value === undefined || value === '') {
        return shallowUpdate({ [filter]: undefined });
      } else {
        const tokens = suffix ? [suffix, operator, value] : [operator, value];

        // Create a new updated shallow copy
        return shallowUpdate({ [filter]: tokens });
      }
    });
  }

  removeFilter(filter: string) {
    this.removeState(filter);
    this._filters.update((filters) => {
      const newFilters = Object.assign({}, filters);
      delete newFilters[filter];
      return newFilters;
    });
  }

  resetFilters() {
    this._filters.set({});
    localStorage.removeItem(this.getStoreKey());
    localStorage.removeItem(this.getStateKey());
    if (this.config.syncUrl) this.syncUrl();
    this.stateWatcher.next({ filter: null, state: undefined });
  }

  private getStoreKey({
    state,
    sort,
    search,
    views,
  }: {
    state?: boolean;
    sort?: boolean;
    search?: boolean;
    views?: boolean;
  } = {}) {
    const key = this.configKey ?? untracked(this.url);
    if (!key) {
      throw new Error(
        `Can't store filters without an activated route or a configured key.`,
      );
    }
    const base = cyrb53(key).toString(16);
    if (state) return `${base}:state`;
    if (sort) return `${base}:sort`;
    if (search) return `${base}:search`;
    if (views) return `${base}:views`;
    return base;
  }

  private getStateKey() {
    return this.getStoreKey({ state: true });
  }

  getFilter(filterName: string) {
    const filters = this.getFilters();
    if (filters === undefined) {
      throw new Error('Filters not ready yet. Wait for `ngOnInit`.');
    }
    const filterTokens = filters[filterName];
    return unpackFilterValue(filterTokens);
  }

  getFilterOperator(
    filterName: string,
    defaultOperator = FilterOperator.LIKE,
  ): FilterCommand {
    const filters = this.getFilters();
    if (filters === undefined) {
      throw new Error('Filters not ready yet. Wait for `ngOnInit`.');
    }
    const filterTokens = filters[filterName];
    if (filterTokens === undefined) {
      return defaultOperator;
    } else {
      if (filterTokens.length == 2 && filterTokens[1] !== FilterOperator.NULL) {
        return filterTokens[0];
      } else {
        return filterTokens.slice(0, 2);
      }
    }
  }

  getAllStoredState() {
    const key = this.getStateKey();
    const rawState = localStorage.getItem(key);
    return rawState === null ? null : JSON.parse(rawState);
  }

  getFilterState(filter: string) {
    const states = this.getAllStoredState();
    return states?.[filter];
  }

  storeState(filter: string, state: any) {
    this.storeAllState({ [filter]: state });
    this.stateWatcher.next({ filter, state });
  }

  private storeAllState(states: Record<string, any>, sync?: boolean) {
    const key = this.getStateKey();
    const allStates = this.getAllStoredState() ?? {};

    Object.assign(allStates, states);
    localStorage.setItem(key, JSON.stringify(allStates));
    if (this.config.syncUrl && sync !== false) this.syncUrl();
  }

  private overwriteStoredFilters(filters: Record<string, any> | undefined) {
    const key = this.getStoreKey();
    localStorage.setItem(key, JSON.stringify(filters ?? null));
  }

  private overwriteStoredSorts(sorts: SortQuery[] | undefined) {
    const key = this.getStoreKey({ sort: true });
    localStorage.setItem(key, JSON.stringify(sorts ?? null));
  }

  private overwriteStoredSearch(search: string | undefined) {
    const key = this.getStoreKey({ search: true });
    if (search) {
      localStorage.setItem(key, search);
    } else {
      localStorage.removeItem(key);
    }
  }

  private overwriteStoredViews(views: {
    view: string | undefined;
    views: Record<string, string[]>;
  }) {
    const key = this.getStoreKey({ views: true });
    localStorage.setItem(key, JSON.stringify(views ?? { views: {} }));
  }

  private syncUrl() {
    if (!this.config.syncUrl)
      throw Error('Sync not available with current configuration');

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        f: this.getFiltersQueryParam(),
        fs: this.getFilterStatesQueryParam(),
        s: this.getSortsQueryParam(),
        q: this.getSearchQueryParam(),
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  getFiltersQueryParam() {
    const filters = untracked(this._filters);
    if (!filters || !Object.keys(filters).length) return;

    return encode(JSON.stringify(filters));
  }

  getFilterStatesQueryParam() {
    const state = this.getAllStoredState();
    if (!state || !Object.keys(state).length) return;

    return encode(JSON.stringify(state));
  }

  getSortsQueryParam() {
    const sorts = untracked(this._sorts);
    if (!sorts || !sorts.length) return;

    return encode(JSON.stringify(sorts));
  }

  getSearchQueryParam() {
    const search = untracked(this._search);
    if (!search || !search.length) return;

    return encode(search);
  }

  removeState(filter: string) {
    const allStates = this.getAllStoredState();
    if (allStates !== null) {
      delete allStates[filter];
      localStorage.setItem(this.getStateKey(), JSON.stringify(allStates));
    }
    this.stateWatcher.next({ filter, state: undefined });
  }

  removeAllState() {
    localStorage.setItem(this.getStateKey(), JSON.stringify({}));
    this.stateWatcher.next({ filter: null, state: undefined });
  }

  getFilters() {
    return this._filters();
  }

  watchFilterValue<T = any>(name: string): Signal<T | undefined> {
    return computed(() => {
      if (this.filters()) return this.getFilter(name);
    });
  }

  watchFilterState<T = any>(name: string): Signal<T | undefined> {
    return toSignal(
      this.stateWatcher.pipe(
        // filter = null when ALL state has been removed, so all watchers should fire
        filter(({ filter }) => filter === null || filter === name),
        map(({ state }) => state),
      ),
    );
  }

  watchSorts(name: string) {
    return computed(() => {
      const sorts = this.sorts() ?? [];
      const order = sorts.find((s) => s[0] === name)?.[1];
      return order
        ? order === 'ASC'
          ? SortState.Ascending
          : SortState.Descending
        : SortState.None;
    });
  }

  cycleSort(name: string) {
    this._sorts.update((sorts) => {
      if (!sorts) return;
      const sortIndex = sorts.findIndex((s) => s[0] === name);
      if (sortIndex === -1) sorts.push([name, 'ASC']);
      else {
        const currentSort = sorts[sortIndex];
        if (currentSort[1] === 'ASC') currentSort[1] = 'DESC';
        else sorts.splice(sortIndex, 1);
      }
      return [...sorts];
    });
  }

  setSearch(query: string) {
    this._search.set(query);
  }

  createCustomView(viewName: string, customView: string[]) {
    this._views.update((views) => {
      views.views[viewName] = customView;
      return { ...views, views: { ...views.views } };
    });
  }

  setView(viewName: string) {
    this._views.update((views) => {
      views.view = viewName;
      return { ...views, views: { ...views.views } };
    });
  }

  clearView() {
    this._views.update((views) => {
      views.view = undefined;
      return { ...views, views: { ...views.views } };
    });
  }

  clearFilters() {
    this.removeAllState();
    this._filters.set({});
  }

  clearSorts() {
    this._sorts.set([]);
  }

  defaultSimplyArrayFilter = (value: string) => [
    FilterSuffix.OR,
    FilterOperator.LIKE,
    value,
  ];

  // fixme: This approach gives false positives when substrings are found
  //  e.g.: will count `notAThing` when looking for `thing` because `thing`
  //  is a substring of `notAThing. Requires adding support to TypeORM/nestjs-paginate
  //  for the $contains operator for simple-array types in SQLite.
  setSimpleArrayFilter<T>(
    filter: string,
    values: T[],
    apply: (value: T) => any = this.defaultSimplyArrayFilter as (
      value: T,
    ) => any,
  ) {
    this._filters.update((filters) => {
      const shallowUpdate = (updated: object) =>
        Object.assign({}, filters, updated);
      if (!values.length) return shallowUpdate({ [filter]: undefined });
      return shallowUpdate({
        [filter]: values.map(apply),
      });
    });
  }
}
